/**
 * Copy for an employer action notice, written for the reader in front of us.
 *
 * AJT posts a note into the thread whenever the employer acts on a candidate —
 * shortlist, interview invite, hired, resume download, contact view. The note
 * arrives as a Stream `system` message carrying a `custom_type` naming the
 * action, and a `text` written for the *jobseeker*: "Acme Corp downloaded your
 * resume."
 *
 * A Stream message is per-channel, not per-viewer, so both sides receive that
 * one string. chat-emp is the employer's app and only ever renders the
 * employer's side, so it swaps in its own wording: the employer reading "Acme
 * Corp downloaded your resume" is reading about themselves in the third person,
 * addressed as though they were the candidate.
 */
const EMPLOYER_COPY = new Map<string, string>([
  ['employer_shortlisted', 'You shortlisted this candidate.'],
  ['employer_invited_interview', 'You invited this candidate to an interview.'],
  ['employer_marked_hired', 'You marked this candidate as hired.'],
  ['employer_downloaded_resume', "You downloaded this candidate's resume."],
  // Deliberately broader than the tag it answers to. AJT fires
  // `employer_viewed_contact` for the whole unlock — the "View Resume" click
  // included — so naming contact details specifically told employers they had
  // done something they had not. "Profile" is true of every path into it.
  ['employer_viewed_contact', "You viewed this candidate's profile."],
  // The other direction: these two narrate what the CANDIDATE did, so the wire
  // text ("You accepted this chat request.") is addressed to them and reads
  // backwards here. Posted by the chat backend when it seals the outreach
  // card's button row — see `NarrateCTA`.
  ['chat_request_accepted', 'This candidate accepted your chat request.'],
  ['chat_request_declined', 'This candidate declined your chat request.'],
]);

/**
 * The one notice the action map above cannot reach.
 *
 * Every hiring-stage write — ours from Quick Actions included — ends at ajt2.5's
 * `ApplicationStatusSignalService`, which posts the note itself. It sends
 * `custom_type: 'system'`, the literal string, for every stage: not an action
 * tag, so `EMPLOYER_COPY` misses and always will, whatever stage was written.
 * The stage rides in `metadata.status` instead, which the chat backend flattens
 * onto the Stream message — but onto `status`, a name Stream's own message
 * response already defines, so it is not a field to read a hiring stage back
 * out of. The text is what arrives intact:
 *
 *   "Your job application status has been updated to: Shortlisted."
 *
 * — `ucfirst($status)` into a hard-coded English sentence, so matching it reads
 * the stage from the one place it survives. Safe to key on because only a note
 * already established as the platform's own gets here (see `isSystemNote`): a
 * member authors under their own id and cannot write as the sentinel, so this
 * is not a sentence a candidate can type their way into.
 *
 * Delete all of it the day AJT sends a real `custom_type` — a one-line change
 * there, `'system'` → `'employer_shortlisted'` and friends — and the map above
 * answers on its own.
 */
const STATUS_NOTICE = /^\s*your job application status has been updated to:\s*([^.]+?)\.?\s*$/i;

const STAGE_COPY = new Map<string, string>([
  ['shortlisted', 'You shortlisted this candidate.'],
  ['interview', 'You moved this candidate to interview.'],
  ['hired', 'You marked this candidate as hired.'],
  ['rejected', 'You rejected this candidate.'],
]);

/**
 * The employer-facing line for a status-change note, or `undefined` if this is
 * not one.
 *
 * An unknown stage still reads in the employer's voice rather than the
 * candidate's — AJT writes the stages, and a new one is a string we have never
 * seen, not a reason to hand the employer "your job application" again.
 */
function stageNoticeText(text: string): string | undefined {
  const stage = STATUS_NOTICE.exec(text)?.[1]?.trim();
  if (!stage) return undefined;
  return STAGE_COPY.get(stage.toLowerCase()) ?? `You updated this candidate's status to ${stage}.`;
}

/**
 * The employer-facing line for a notice, or the message's own text.
 *
 * Falling back rather than blanking is the contract with AJT: `custom_type` is
 * an open string the backend does not restrict, so a new action type can ship
 * from AJT with no release here. Such a note still reads correctly — just in the
 * jobseeker's words — where dropping it would lose the record entirely, and
 * throwing would take the thread with it.
 *
 * A `Map` and not an object literal because `custom_type` is upstream input:
 * an object would answer `'constructor'` or `'toString'` out of its prototype
 * and render the source of a function into the thread.
 */
export function systemNoticeText(customType: unknown, text: string): string {
  const byAction = typeof customType === 'string' ? EMPLOYER_COPY.get(customType) : undefined;
  // The action tag first: it names what happened, where the sentence below only
  // describes the stage the action left behind.
  return byAction ?? stageNoticeText(text) ?? text;
}
